
































































import {
	computed,
	defineComponent,
	PropType,
	ref,
} from '@vue/composition-api';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import { syncedRef } from '@front-app/util/reactivity';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import TrForm from '@front-app/components/form/Form.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import YearMonthDayInput from '@front-app/components/inputs/YearMonthDayInput.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import TimingTypeSelector from './edit/TimingTypeSelector.vue';
import { DigestMonthlyTiming, DigestTimingDay } from '../model';
import {
	determineScheduleType,
	DigestScheduleType,
	getAllFlattenedDays,
	getFirstWeekWithDays,
	getMonthlySameDayTiming,
	getMonthWeekFromDate,
	getWeekDayFromDate,
	getWeeklySameDayTiming,
} from '../util/scheduling';
import {
	DigestFormParams,
	DigestFormOutput,
} from './digestForm';
import MonthlyTimingCustom from './edit/MonthlyTimingCustom.vue';

function createEmptyDigest(): DigestFormParams {
	return {
		startDate: undefined,
		timingMonthly: getEmptyTimingSchedule(),
		timingPeriodic: -1,
	};
}

function getEmptyTimingSchedule(): DigestMonthlyTiming {
	return {
		firstWeek: new Set(),
		secondWeek: new Set(),
		thirdWeek: new Set(),
		fourthWeek: new Set(),
		lastWeek: new Set(),
	};
}

export default defineComponent({
	components: {
		TrForm,
		FormSection,
		FormLabel,
		YearMonthDayInput,
		TimingTypeSelector,
		MonthlyTimingCustom,
		CtaButton,
		FormError,
	},
	props: {
		digest: {
			type: Object as PropType<DigestFormParams>,
			default: createEmptyDigest,
		},
		submitButtonText: {
			type: String,
			required: true,
		},
		isSubmitting: Boolean,
	},
	setup(props, ctx) {
		const startDate = syncedRef(() => (props.digest.startDate ? new Date(props.digest.startDate) : null)).ref;
		const timingMonthly = syncedRef(() => props.digest.timingMonthly).ref;
		const timingPeriodic = syncedRef(() => props.digest.timingPeriodic).ref;

		const showCustomPicker = ref(false);

		const selectedScheduleType = computed(() => determineScheduleType(timingMonthly.value, timingPeriodic.value));
		const currentMonthWeek = computed(() => {
			switch (selectedScheduleType.value) {
			case DigestScheduleType.CUSTOM:
			case DigestScheduleType.UNSCHEDULED:
			case DigestScheduleType.PERIODIC:
			case DigestScheduleType.WEEKLY_SAME_DAY:
				// Here we need to fall back to defaults, because the week isn't specified
				// in the timing
				return startDate.value ? getMonthWeekFromDate(startDate.value) : 'firstWeek';
			case DigestScheduleType.MONTHLY_SAME_DAY:
				// Return the selected week from the timing
				// eslint-disable-next-line no-case-declarations
				const selectedWeek = getFirstWeekWithDays(timingMonthly.value);
				if (selectedWeek) {
					return selectedWeek;
				}
				return startDate.value ? getMonthWeekFromDate(startDate.value) : 'firstWeek';
			default:
				// eslint-disable-next-line
				const exhaustiveCheck: never = selectedScheduleType.value;
				throw new Error(`Unhandled scheduler type case: ${exhaustiveCheck}`);
			}
		});
		const currentWeekDay = computed(() => {
			switch (selectedScheduleType.value) {
			case DigestScheduleType.CUSTOM:
			case DigestScheduleType.UNSCHEDULED:
			case DigestScheduleType.PERIODIC:
				// Here we need to fall back to defaults, because the day isn't specified
				// in the timing
				return startDate.value ? getWeekDayFromDate(startDate.value) : DigestTimingDay.SUNDAY;
			case DigestScheduleType.MONTHLY_SAME_DAY:
			case DigestScheduleType.WEEKLY_SAME_DAY:
				// Return the selected day from the timing
				return getAllFlattenedDays(timingMonthly.value)[0];
			default:
				// eslint-disable-next-line
				const exhaustiveCheck: never = selectedScheduleType.value;
				throw new Error(`Unhandled scheduler type case: ${exhaustiveCheck}`);
			}
		});
		const handleTypeSelection = (nextType: DigestScheduleType) => {
			switch (nextType) {
			case DigestScheduleType.MONTHLY_SAME_DAY:
				showCustomPicker.value = false;
				timingMonthly.value = getMonthlySameDayTiming(currentMonthWeek.value, currentWeekDay.value);
				timingPeriodic.value = -1;
				return;
			case DigestScheduleType.WEEKLY_SAME_DAY:
				showCustomPicker.value = false;
				timingMonthly.value = getWeeklySameDayTiming(currentWeekDay.value);
				timingPeriodic.value = -1;
				return;
			case DigestScheduleType.CUSTOM:
				showCustomPicker.value = true;
				timingPeriodic.value = -1;
				return;
			case DigestScheduleType.UNSCHEDULED:
			case DigestScheduleType.PERIODIC:
				// eslint-disable-next-line no-console
				console.error('Unhandled DigestScheduleType selection: ', nextType);
				return;
			default:
				// eslint-disable-next-line
				const exhaustiveCheck: never = nextType;
				throw new Error(`Unhandled scheduler type case: ${exhaustiveCheck}`);
			}
		};

		// Validation
		const validation = v({
			startDate: {
				value: startDate,
				rules: [required()],
			},
			selectedScheduleType: {
				value: selectedScheduleType,
				rules: [required([DigestScheduleType.UNSCHEDULED])],
			},
		});

		// Submission
		const focusInvalid = useFocusFirstInvalidField({ containerSelector: '#digest-form' });
		const submit = () => {
			// validation
			validation.validate();

			if (!startDate.value || !validation.isValid.value) {
				focusInvalid.focusFirstInvalid();
				return;
			}

			const payload: DigestFormOutput = {
				startDate: startDate.value?.toISOString(),
				timingMonthly: timingMonthly.value,
				timingPeriodic: timingPeriodic.value,
			};
			ctx.emit('submit', payload);
		};

		return {
			// Primary data
			validation,
			startDate,
			timingMonthly,
			timingPeriodic,
			// Derived data
			selectedScheduleType,
			currentMonthWeek,
			currentWeekDay,
			// Custom picker
			showCustomPicker,
			// Event handlers
			handleTypeSelection,
			submit,
			// Util
			DigestScheduleType,
			Size,
			ButtonPalettes,
		};
	},
});
